import * as v from 'valibot';

import {
  ACTIVE_CASE_STATUSES,
  activeCasesApi,
  activeCaseSchema,
  newCasesApi,
  newCaseSchema,
  type ActiveCase,
  type NewCase,
} from '#entities';
import { advance, reportThrottled, resourceUpdated } from '#shared';

import { hasErrorIssue } from '#shared/api';

import type { AppDispatch } from '#shared';
import type { EntityFrame } from './serverMessage';

const REPORT_THROTTLE_MS = 60_000;

const removeById = <T extends { id: string }>(list: T[], id: string): T[] =>
  list.filter(item => item.id !== id);

const upsertSortedActive = (
  list: ActiveCase[],
  entity: ActiveCase,
): ActiveCase[] =>
  [...removeById(list, entity.id), entity].sort(
    (a, b) =>
      ACTIVE_CASE_STATUSES.indexOf(a.status) -
      ACTIVE_CASE_STATUSES.indexOf(b.status),
  );

const dropFromActive = (dispatch: AppDispatch, id: string): void => {
  dispatch(
    activeCasesApi.util.updateQueryData('getActiveCases', undefined, draft => {
      draft.data = removeById(draft.data, id);
    }),
  );
  dispatch(resourceUpdated({ resource: 'activeCases', atMs: Date.now() }));
};

const dropFromNew = (dispatch: AppDispatch, id: string): void => {
  dispatch(
    newCasesApi.util.updateQueryData('getNewCases', undefined, draft => {
      draft.data = removeById(draft.data, id);
    }),
  );
  dispatch(resourceUpdated({ resource: 'newCases', atMs: Date.now() }));
};

type CaseList = 'active' | 'new';

// Same call as caseCatchup's resync. RTK already refuses a second request while
// one is pending (E015), but a case that stays invalid upstream is rejected on
// every frame, so the refetch is also throttled per list. The trailing call
// keeps a reject inside the window from being dropped silently.
const REFETCH_THROTTLE_MS = 60_000;

const lastRefetchMs: Record<CaseList, number> = { active: 0, new: 0 };
const trailingRefetch: Partial<
  Record<CaseList, ReturnType<typeof setTimeout>>
> = {};

const runRefetch = (list: CaseList, dispatch: AppDispatch): void => {
  lastRefetchMs[list] = Date.now();
  const options = { subscribe: false, forceRefetch: true };
  if (list === 'active') {
    void dispatch(
      activeCasesApi.endpoints.getActiveCases.initiate(undefined, options),
    );
    return;
  }
  void dispatch(newCasesApi.endpoints.getNewCases.initiate(undefined, options));
};

const refetchList = (list: CaseList, dispatch: AppDispatch): void => {
  const waitMs = lastRefetchMs[list] + REFETCH_THROTTLE_MS - Date.now();
  if (waitMs <= 0) {
    runRefetch(list, dispatch);
    return;
  }
  if (trailingRefetch[list] === undefined) {
    trailingRefetch[list] = setTimeout(() => {
      trailingRefetch[list] = undefined;
      runRefetch(list, dispatch);
    }, waitMs);
  }
};

/** For test cleanup. Clears both lists' throttle windows and pending trailing refetches. */
export const __resetEntityRefetchThrottle = (): void => {
  for (const list of ['active', 'new'] as const) {
    lastRefetchMs[list] = 0;
    clearTimeout(trailingRefetch[list]);
    trailingRefetch[list] = undefined;
  }
};

/**
 * Membership rule (spec §6): a definite `list` upserts there and removes from the other; null/removed drops both.
 * A frame is rejected only for an ERROR-severity issue, the rule REST applies (`hasErrorIssue`),
 * so live and reload agree; a rejected frame refetches its list (TMDXNC-500). The raw `entity`
 * is applied, not `parsed.output`, to match the HTTP-cached shape.
 */
export const applyEntityFrame = (
  frame: EntityFrame,
  dispatch: AppDispatch,
): void => {
  const { entityId, list, entity, removed, cursor } = frame;

  if (removed || list === null || entity === null) {
    dropFromActive(dispatch, entityId);
    dropFromNew(dispatch, entityId);
    advance('case', cursor);
    return;
  }

  if (list === 'active') {
    const parsed = v.safeParse(activeCaseSchema, entity);
    if (hasErrorIssue(parsed.issues)) {
      reportThrottled(
        `nop-events:bad-active-entity:${entityId}`,
        'nop-events: invalid active-case entity frame',
        {
          throttleMs: REPORT_THROTTLE_MS,
          level: 'warning',
          extra: { entityId, list, issues: parsed.issues },
        },
      );
      refetchList('active', dispatch);
      return;
    }
    dispatch(
      activeCasesApi.util.updateQueryData(
        'getActiveCases',
        undefined,
        draft => {
          draft.data = upsertSortedActive(draft.data, entity as ActiveCase);
        },
      ),
    );
    dispatch(resourceUpdated({ resource: 'activeCases', atMs: Date.now() }));
    dropFromNew(dispatch, entityId);
    advance('case', cursor);
    return;
  }

  const parsed = v.safeParse(newCaseSchema, entity);
  if (hasErrorIssue(parsed.issues)) {
    reportThrottled(
      `nop-events:bad-new-entity:${entityId}`,
      'nop-events: invalid new-case entity frame',
      {
        throttleMs: REPORT_THROTTLE_MS,
        level: 'warning',
        extra: { entityId, list, issues: parsed.issues },
      },
    );
    refetchList('new', dispatch);
    return;
  }
  dispatch(
    newCasesApi.util.updateQueryData('getNewCases', undefined, draft => {
      draft.data = [...removeById(draft.data, entityId), entity as NewCase];
    }),
  );
  dispatch(resourceUpdated({ resource: 'newCases', atMs: Date.now() }));
  dropFromActive(dispatch, entityId);
  advance('case', cursor);
};
